import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TrendingDown, Loader2, Save, Search, Percent } from 'lucide-react';

import {
    fetchCCListForDepreciation,
    fetchDepreciationWefDate,
    fetchCCDepreciationValue,
    fetchDepreciationGrid,
    submitDepreciationValue,
    submitDepreciationPercentage,
    clearCCDepreciationValue,
    clearDepreciationValueSaveResult,
    clearDepreciationPercentageSaveResult,
    selectDepCCList,
    selectDepWefDate,
    selectDepCurrentCCValue,
    selectDepValueList,
    selectDepPercentageList,
    selectDepValueSaveResult,
    selectDepPercentageSaveResult,
    selectDepCCListLoading,
    selectDepCCValueLoading,
    selectDepGridLoading,
    selectDepGridError,
    selectDepSaveValueLoading,
    selectDepSaveValueError,
    selectDepSavePercentageLoading,
    selectDepSavePercentageError,
} from '../../slices/accountsSlice/depreciationConfigSlice';
import { showToast } from '../../utilities/toastUtilities';

const currencyFmt = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
});

// "dd-MMM-yyyy" (e.g. "02-Apr-2019") -> "yyyy-mm-dd" for native date inputs
const ddMonYyyyToIso = (str) => {
    if (!str) return '';
    const [d, mon, y] = str.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const m = months.indexOf(mon) + 1;
    if (!d || !m || !y) return '';
    return `${y}-${String(m).padStart(2, '0')}-${d.padStart(2, '0')}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const DepreciationConfig = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';
    const roleId = userData?.roleId || userData?.RID || 0;

    const ccList        = useSelector(selectDepCCList);
    const wefDate        = useSelector(selectDepWefDate);
    const currentCCValue = useSelector(selectDepCurrentCCValue);
    const valueList       = useSelector(selectDepValueList);
    const percentageList  = useSelector(selectDepPercentageList);
    const valueSaveResult      = useSelector(selectDepValueSaveResult);
    const percentageSaveResult = useSelector(selectDepPercentageSaveResult);

    const ccListLoading  = useSelector(selectDepCCListLoading);
    const ccValueLoading = useSelector(selectDepCCValueLoading);
    const gridLoading    = useSelector(selectDepGridLoading);
    const gridError      = useSelector(selectDepGridError);
    const saveValueLoading = useSelector(selectDepSaveValueLoading);
    const saveValueError   = useSelector(selectDepSaveValueError);
    const savePercentLoading = useSelector(selectDepSavePercentageLoading);
    const savePercentError   = useSelector(selectDepSavePercentageError);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedCC, setSelectedCC] = useState('');
    const [ccValueInput, setCcValueInput] = useState('');
    const searchRef = useRef(null);

    const [newWefDate, setNewWefDate] = useState('');
    const [newRate, setNewRate] = useState('');

    const load = () => {
        dispatch(fetchCCListForDepreciation());
        dispatch(fetchDepreciationWefDate());
        dispatch(fetchDepreciationGrid());
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    useEffect(() => {
        if (wefDate) setNewWefDate(ddMonYyyyToIso(wefDate));
    }, [wefDate]);

    useEffect(() => {
        setCcValueInput(currentCCValue ?? '');
    }, [currentCCValue]);

    useEffect(() => {
        if (valueSaveResult) {
            showToast('success', 'Depreciation value saved successfully.');
            dispatch(clearDepreciationValueSaveResult());
            setSearchQuery('');
            setSelectedCC('');
            setCcValueInput('');
            dispatch(clearCCDepreciationValue());
            load();
        }
        if (saveValueError) {
            showToast('error', typeof saveValueError === 'string' ? saveValueError : 'Failed to save the depreciation value.');
            dispatch(clearDepreciationValueSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueSaveResult, saveValueError, dispatch]);

    useEffect(() => {
        if (percentageSaveResult) {
            showToast('success', 'Depreciation percentage saved successfully.');
            dispatch(clearDepreciationPercentageSaveResult());
            setNewRate('');
            load();
        }
        if (savePercentError) {
            showToast('error', typeof savePercentError === 'string' ? savePercentError : 'Failed to save the depreciation percentage.');
            dispatch(clearDepreciationPercentageSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [percentageSaveResult, savePercentError, dispatch]);

    useEffect(() => {
        const handler = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const suggestions = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return [];
        return ccList.filter(cc => cc.CC_Name?.toLowerCase().includes(q)).slice(0, 8);
    }, [ccList, searchQuery]);

    const currentPercentageRow = useMemo(() => {
        if (percentageList.length === 0) return null;
        return [...percentageList].sort((a, b) => new Date(ddMonYyyyToIso(b.wefdate)) - new Date(ddMonYyyyToIso(a.wefdate)))[0];
    }, [percentageList]);

    const sortedPercentageHistory = useMemo(
        () => [...percentageList].sort((a, b) => new Date(ddMonYyyyToIso(b.wefdate)) - new Date(ddMonYyyyToIso(a.wefdate))),
        [percentageList]
    );

    const ccNameByCode = useMemo(() => {
        const map = new Map();
        ccList.forEach(cc => map.set(cc.CC_Code, cc.CC_Name));
        return map;
    }, [ccList]);

    const handleSelectCC = (cc) => {
        setSearchQuery(cc.CC_Name);
        setSelectedCC(cc.CC_Code);
        setShowSuggestions(false);
        dispatch(fetchCCDepreciationValue(cc.CC_Code));
    };

    const handleSubmitValue = () => {
        if (!selectedCC) { showToast('error', 'Please select a Cost Centre.'); return; }
        const val = parseFloat(ccValueInput);
        if (ccValueInput === '' || isNaN(val) || val < 0) { showToast('error', 'Please enter a valid value.'); return; }

        dispatch(submitDepreciationValue({
            CostCenter: selectedCC,
            Value:      val,
            Createdby:  currentUser,
            RoleId:     roleId,
        }));
    };

    const handleSubmitPercentage = () => {
        if (!newWefDate) { showToast('error', 'Please select a WEF Date.'); return; }
        const rate = parseFloat(newRate);
        if (newRate === '' || isNaN(rate) || rate <= 0) { showToast('error', 'Please enter a valid depreciation percentage.'); return; }

        dispatch(submitDepreciationPercentage({
            wefdate:      newWefDate,
            InterestRate: String(rate),
            Createdby:    currentUser,
            RoleId:       roleId,
        }));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* Page Header */}
            <div className="max-w-5xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 text-white">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20 flex-shrink-0">
                            <TrendingDown className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">
                                Configuration
                            </span>
                            <h1 className="text-xl md:text-2xl font-black tracking-tight mt-1">Depreciation Configuration</h1>
                            <p className="text-indigo-200 text-sm mt-0.5">
                                One shared depreciation percentage, and a depreciation value set individually per cost centre.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto space-y-4">

                {/* Depreciation Percentage — single, WEF-dated value */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                        <Percent className="h-4 w-4 text-indigo-500" />
                        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">Depreciation Percentage</h2>
                    </div>

                    <div className="p-4 md:p-5">
                        <div className="flex items-center gap-4 mb-4 p-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800/40">
                            <div>
                                <p className="text-[11px] font-bold text-indigo-500 uppercase tracking-wider">Current Rate</p>
                                {gridLoading ? (
                                    <Loader2 className="h-4 w-4 text-indigo-500 animate-spin mt-1" />
                                ) : currentPercentageRow ? (
                                    <p className="text-2xl font-black text-indigo-700 dark:text-indigo-400">{currentPercentageRow.InterestRate}%</p>
                                ) : (
                                    <p className="text-sm text-gray-400 mt-1">Not configured yet</p>
                                )}
                            </div>
                            {currentPercentageRow && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    effective from <span className="font-semibold text-gray-700 dark:text-gray-300">{currentPercentageRow.wefdate}</span>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    WEF Date
                                </label>
                                <input
                                    type="date"
                                    value={newWefDate}
                                    min={ddMonYyyyToIso(wefDate)}
                                    max={todayISO()}
                                    onChange={(e) => setNewWefDate(e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    Depreciation %
                                </label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={newRate}
                                    onChange={(e) => setNewRate(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitPercentage(); }}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all"
                                />
                            </div>
                            <button
                                onClick={handleSubmitPercentage}
                                disabled={savePercentLoading}
                                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {savePercentLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                Submit
                            </button>
                        </div>

                        {sortedPercentageHistory.length > 1 && (
                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">History</p>
                                <div className="space-y-1">
                                    {sortedPercentageHistory.map((row, idx) => (
                                        <div key={`${row.wefdate}-${idx}`} className="flex items-center justify-between text-xs px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-900/30">
                                            <span className="text-gray-500 dark:text-gray-400">{row.wefdate}</span>
                                            <span className="font-semibold text-gray-700 dark:text-gray-300">{row.InterestRate}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Cost Centre Depreciation Value */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                        <TrendingDown className="h-4 w-4 text-indigo-500" />
                        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">Cost Centre Depreciation Value</h2>
                    </div>

                    <div className="p-4 md:p-5">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div className="md:col-span-2 relative" ref={searchRef}>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    Cost Centre
                                </label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); setSelectedCC(''); }}
                                        onFocus={() => searchQuery && setShowSuggestions(true)}
                                        disabled={ccListLoading}
                                        placeholder="Search or select a cost centre…"
                                        className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60"
                                    />
                                </div>
                                {showSuggestions && suggestions.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-xl border-2 border-indigo-200 dark:border-indigo-700 shadow-xl z-50 max-h-64 overflow-y-auto">
                                        {suggestions.map((cc) => (
                                            <button
                                                key={cc.CC_Code}
                                                type="button"
                                                onClick={() => handleSelectCC(cc)}
                                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0 text-gray-800 dark:text-gray-100"
                                            >
                                                {cc.CC_Name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {showSuggestions && searchQuery.trim() && suggestions.length === 0 && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 shadow-xl z-50 px-4 py-3 text-center text-sm text-gray-400">
                                        No cost centres found
                                    </div>
                                )}
                            </div>

                            <div className="flex items-end gap-2">
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                        Value
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={ccValueInput}
                                        onChange={(e) => setCcValueInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitValue(); }}
                                        disabled={!selectedCC || ccValueLoading}
                                        placeholder="0.00"
                                        className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60"
                                    />
                                </div>
                                <button
                                    onClick={handleSubmitValue}
                                    disabled={!selectedCC || saveValueLoading}
                                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {saveValueLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>

                    {gridLoading ? (
                        <div className="py-10 flex flex-col items-center justify-center gap-2">
                            <Loader2 className="h-5 w-5 text-indigo-500 animate-spin" />
                            <span className="text-xs text-gray-400">Loading configured values…</span>
                        </div>
                    ) : gridError ? (
                        <div className="py-8 text-center text-sm text-rose-500">{gridError}</div>
                    ) : valueList.length === 0 ? (
                        <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm border-t border-gray-100 dark:border-gray-700">
                            No cost centre values configured yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto border-t border-gray-100 dark:border-gray-700">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cost Centre</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Value</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {valueList.map((row, idx) => (
                                        <tr key={`${row.CostCenter}-${idx}`} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                            <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                                                {ccNameByCode.get(row.CostCenter) || row.CostCenter}
                                            </td>
                                            <td className="px-4 py-3 text-right font-semibold text-indigo-700 dark:text-indigo-400">
                                                {row.Value !== '' ? currencyFmt.format(row.Value) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DepreciationConfig;
